import React, { useState, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { AlertTriangle, Plus, Trash2, Loader2, ShieldCheck, User as UserIcon } from 'lucide-react';

function getApiBaseUrl() {
  const env = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (env) return env;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, '');
  }
  return '';
}

const SEVERITY_OPTIONS = [
  { value: 'low', labelKey: 'manual_damage_severity_low', fallback: 'Leve' },
  { value: 'medium', labelKey: 'manual_damage_severity_medium', fallback: 'Moderado' },
  { value: 'high', labelKey: 'manual_damage_severity_high', fallback: 'Grave' },
];

const TYPE_SUGGESTIONS = [
  { value: 'rayón', labelKey: 'manual_damage_type_scratch', fallback: 'Rayón' },
  { value: 'abolladura', labelKey: 'manual_damage_type_dent', fallback: 'Abolladura' },
  { value: 'rotura', labelKey: 'manual_damage_type_break', fallback: 'Rotura' },
  { value: 'desgaste', labelKey: 'manual_damage_type_wear', fallback: 'Desgaste' },
  { value: 'suciedad', labelKey: 'manual_damage_type_dirt', fallback: 'Suciedad' },
  { value: 'otro', labelKey: 'manual_damage_type_other', fallback: 'Otro' },
];

function inspectionTextField(val) {
  if (val == null) return '';
  return String(val).trim();
}

/** Same human-readable line as backend `_enrich_inspection_for_admin_dashboard`. */
function buildManualDamageLine(d) {
  if (!d || typeof d !== 'object') return '';
  const desc = inspectionTextField(d.description);
  const typ = inspectionTextField(d.type);
  const loc = inspectionTextField(d.location);
  const sev = inspectionTextField(d.severity);
  const parts = [typ, loc, sev, desc].filter(Boolean);
  const tail = parts.length ? parts.join(' · ') : 'daño reportado';
  const sourceLabel = d.source === 'admin' ? 'Admin' : 'Cliente';
  return `[Reportado por ${sourceLabel}] ${tail}`;
}

/**
 * Rebuild `ai_summary.damages_detected` from AI lines + current `manual_damages`,
 * stripping any stale "[Reportado por …]" lines first so add/delete stays consistent.
 */
function mergeManualIntoInspectionSummary(inspection) {
  if (!inspection || typeof inspection !== 'object') return inspection;
  const prev =
    inspection.ai_summary && typeof inspection.ai_summary === 'object'
      ? { ...inspection.ai_summary }
      : {};
  const prevList = Array.isArray(prev.damages_detected) ? [...prev.damages_detected] : [];
  const baseLines = prevList.filter(
    (line) =>
      typeof line !== 'string' || !line.trimStart().startsWith('[Reportado por'),
  );
  const manual = Array.isArray(inspection.manual_damages) ? inspection.manual_damages : [];
  const manualLines = manual.map(buildManualDamageLine).filter(Boolean);
  const nextSummary = {
    ...prev,
    damages_detected: [...baseLines, ...manualLines],
    odometer_reading:
      inspectionTextField(prev.odometer_reading) ||
      inspectionTextField(inspection.odometer_reading) ||
      '',
    fuel_level:
      inspectionTextField(prev.fuel_level) ||
      inspectionTextField(inspection.fuel_level) ||
      '',
    overall_condition:
      inspectionTextField(prev.overall_condition) ||
      inspectionTextField(inspection.overall_condition) ||
      '',
  };
  return { ...inspection, ai_summary: nextSummary };
}

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
}

function severityBadgeClass(severity) {
  switch (severity) {
    case 'high':
      return 'bg-red-100 text-red-700';
    case 'medium':
      return 'bg-orange-100 text-orange-700';
    case 'low':
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
}

/**
 * ManualDamageReporter
 *
 * Shared block used inside inspection detail modals (admin + client).
 * Lists the manually-reported damages stored on the inspection and lets the
 * current user add a new one. Admins can delete any entry; clients can only
 * delete entries they themselves authored.
 *
 * Props:
 *   - inspection: the full inspection object (must include `id` and may include
 *     `manual_damages` array). Updates are applied via the `onUpdated` callback.
 *   - role: 'admin' | 'client' — chooses which token to send.
 *   - currentUserId: optional, used to gate the delete button on the client side.
 *   - onUpdated(updatedInspection): called with the patched inspection so the
 *     parent can refresh its local copy without re-fetching.
 *   - showHeading: optional bool, defaults to true.
 */
export default function ManualDamageReporter({
  inspection,
  role,
  currentUserId,
  onUpdated,
  showHeading = true,
}) {
  const { t } = useTranslation();
  const uid = useId().replace(/:/g, '');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({
    type: '',
    location: '',
    severity: 'medium',
    description: '',
  });

  const damages = Array.isArray(inspection?.manual_damages) ? inspection.manual_damages : [];

  const tokenKey = role === 'admin' ? 'admin_token' : 'session_token';

  const closeDialog = () => {
    setDialogOpen(false);
    setForm({ type: '', location: '', severity: 'medium', description: '' });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!inspection?.id) return;
    const trimmedDesc = form.description.trim();
    const trimmedType = form.type.trim();
    if (!trimmedDesc && !trimmedType) {
      toast.error(
        t('manual_damage_required', {
          defaultValue: 'Indica el tipo o la descripción del daño',
        }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const base = getApiBaseUrl();
      const token = localStorage.getItem(tokenKey);
      const res = await fetch(`${base}/api/inspections/${inspection.id}/manual-damages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: trimmedType,
          location: form.location.trim(),
          severity: form.severity,
          description: trimmedDesc,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(body.detail) || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const newEntry = data.damage;
      const next = mergeManualIntoInspectionSummary({
        ...inspection,
        manual_damages: [...damages, newEntry],
      });
      onUpdated?.(next);
      toast.success(
        t('manual_damage_added', { defaultValue: 'Daño reportado correctamente' }),
      );
      closeDialog();
    } catch (err) {
      console.error('manual damage add failed:', err);
      toast.error(
        t('manual_damage_add_failed', {
          defaultValue: 'No se pudo guardar el daño',
        }) + (err?.message ? `: ${err.message}` : ''),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (damageId) => {
    if (!inspection?.id || !damageId) return;
    if (
      typeof window !== 'undefined' &&
      typeof window.confirm === 'function' &&
      !window.confirm(
        t('manual_damage_confirm_delete', {
          defaultValue: '¿Eliminar este daño reportado?',
        }),
      )
    ) {
      return;
    }
    setDeletingId(damageId);
    try {
      const base = getApiBaseUrl();
      const token = localStorage.getItem(tokenKey);
      const res = await fetch(
        `${base}/api/inspections/${inspection.id}/manual-damages/${damageId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(body.detail) || `HTTP ${res.status}`);
      }
      const next = mergeManualIntoInspectionSummary({
        ...inspection,
        manual_damages: damages.filter((d) => d?.id !== damageId),
      });
      onUpdated?.(next);
      toast.success(
        t('manual_damage_deleted', { defaultValue: 'Daño eliminado' }),
      );
    } catch (err) {
      console.error('manual damage delete failed:', err);
      toast.error(
        t('manual_damage_delete_failed', {
          defaultValue: 'No se pudo eliminar el daño',
        }) + (err?.message ? `: ${err.message}` : ''),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const canDelete = (entry) => {
    if (!entry) return false;
    if (role === 'admin') return true;
    return Boolean(currentUserId) && entry.author_id === currentUserId;
  };

  return (
    <div className="space-y-3">
      {showHeading && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h4 className="font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            {t('manual_damage_section_title', { defaultValue: 'Daños reportados' })}
            {damages.length > 0 && (
              <Badge variant="outline" className="ml-1">
                {damages.length}
              </Badge>
            )}
          </h4>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setDialogOpen(true)}
            data-testid="report-damage-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('manual_damage_report_btn', { defaultValue: 'Reportar daño' })}
          </Button>
        </div>
      )}
      {!showHeading && (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setDialogOpen(true)}
            data-testid="report-damage-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('manual_damage_report_btn', { defaultValue: 'Reportar daño' })}
          </Button>
        </div>
      )}

      {damages.length === 0 ? (
        <p className="text-sm text-gray-500">
          {t('manual_damage_none', { defaultValue: 'Aún no se ha reportado ningún daño manual.' })}
        </p>
      ) : (
        <div className="space-y-2">
          {damages.map((d) => (
            <Card key={d.id || `${d.created_at}-${d.description}`}>
              <CardContent className="p-3 flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={severityBadgeClass(d.severity)}>
                      {t(`manual_damage_severity_${d.severity}`, {
                        defaultValue:
                          d.severity === 'high'
                            ? 'Grave'
                            : d.severity === 'low'
                            ? 'Leve'
                            : 'Moderado',
                      })}
                    </Badge>
                    {d.type && (
                      <Badge variant="outline">{d.type}</Badge>
                    )}
                    {d.location && (
                      <span className="text-xs text-gray-500">{d.location}</span>
                    )}
                  </div>
                  {d.description && (
                    <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap break-words">
                      {d.description}
                    </p>
                  )}
                  <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    {d.source === 'admin' ? (
                      <ShieldCheck className="w-3 h-3" />
                    ) : (
                      <UserIcon className="w-3 h-3" />
                    )}
                    <span>
                      {d.source === 'admin'
                        ? t('manual_damage_by_admin', { defaultValue: 'Reportado por Admin' })
                        : t('manual_damage_by_client', { defaultValue: 'Reportado por Cliente' })}
                      {d.author_name ? ` · ${d.author_name}` : ''}
                    </span>
                  </div>
                </div>
                {canDelete(d) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(d.id)}
                    disabled={deletingId === d.id}
                    aria-label={t('manual_damage_delete_aria', { defaultValue: 'Eliminar daño' })}
                  >
                    {deletingId === d.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('manual_damage_dialog_title', { defaultValue: 'Reportar daño' })}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor={`manual-damage-type-${uid}`}>
                {t('manual_damage_field_type', { defaultValue: 'Tipo' })}
              </Label>
              <Input
                id={`manual-damage-type-${uid}`}
                list={`manual-damage-type-options-${uid}`}
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                placeholder={t('manual_damage_field_type_placeholder', {
                  defaultValue: 'rayón, abolladura, rotura, etc.',
                })}
              />
              <datalist id={`manual-damage-type-options-${uid}`}>
                {TYPE_SUGGESTIONS.map((opt) => (
                  <option
                    key={opt.value}
                    value={t(opt.labelKey, { defaultValue: opt.fallback })}
                  />
                ))}
              </datalist>
            </div>
            <div>
              <Label htmlFor={`manual-damage-location-${uid}`}>
                {t('manual_damage_field_location', { defaultValue: 'Ubicación' })}
              </Label>
              <Input
                id={`manual-damage-location-${uid}`}
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                placeholder={t('manual_damage_field_location_placeholder', {
                  defaultValue: 'puerta delantera derecha, parachoques, …',
                })}
              />
            </div>
            <div>
              <Label htmlFor={`manual-damage-severity-${uid}`}>
                {t('manual_damage_field_severity', { defaultValue: 'Severidad' })}
              </Label>
              <select
                id={`manual-damage-severity-${uid}`}
                value={form.severity}
                onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {SEVERITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey, { defaultValue: opt.fallback })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor={`manual-damage-description-${uid}`}>
                {t('manual_damage_field_description', { defaultValue: 'Descripción' })}
              </Label>
              <Textarea
                id={`manual-damage-description-${uid}`}
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder={t('manual_damage_field_description_placeholder', {
                  defaultValue: 'Describe brevemente el daño observado',
                })}
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeDialog} disabled={submitting}>
                {t('manual_damage_cancel', { defaultValue: 'Cancelar' })}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    {t('manual_damage_saving', { defaultValue: 'Guardando…' })}
                  </>
                ) : (
                  t('manual_damage_save', { defaultValue: 'Guardar' })
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
